import { useState } from "react";
import { Factory, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, gramas } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

export function DevSendProductionModal({ project, test = null, onClose, onSaved }) {
  const plates = (project.plates || []).filter((p) =>
    test && (test.plate_ids || []).length ? test.plate_ids.includes(p.id) : true
  );
  const [picked, setPicked] = useState(plates.map((p) => p.id));
  const [quantity, setQuantity] = useState(String(test?.quantity || 1));
  const [busy, setBusy] = useState(false);

  const toggle = (id) =>
    setPicked((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const send = async (ids) => {
    if (ids.length === 0) {
      toast.error("Escolha ao menos uma Plate.");
      return;
    }
    setBusy(true);
    try {
      const url = test
        ? `/dev-projects/${project.id}/tests/${test.id}/send-production`
        : `/dev-projects/${project.id}/send-production`;
      const { data } = await api.post(url, {
        plate_ids: ids,
        quantity: Number(quantity || 1),
      });
      onSaved(data);
      toast.success("Enviado para a Produção. O consumo acontece ao concluir a impressão.");
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={test ? `Enviar teste #${test.attempt} para produção` : "Enviar para produção"}
      description="A produção aparece na fila marcada como Desenvolvimento. O estoque só é consumido na conclusão."
      testid="dev-send-production-modal"
    >
      <div className="mt-2 flex flex-col gap-4">
        <Field label="Quantidade por Plate">
          <Input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="font-num"
            data-testid="send-quantity-input"
          />
        </Field>

        {plates.length === 0 ? (
          <p className="rounded-lg border border-dashed border-white/15 bg-secondary/30 p-3 text-xs text-muted-foreground">
            Este projeto ainda não tem Plate. Crie ou vincule uma Plate antes de imprimir.
          </p>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Plates a imprimir</p>
            {plates.map((p) => {
              const on = picked.includes(p.id);
              return (
                <div
                  key={p.id}
                  className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 ${
                    on ? "border-brand/50 bg-brand/10" : "border-white/10 bg-secondary/40"
                  }`}
                  data-testid={`send-plate-${p.id}`}
                >
                  <button
                    type="button"
                    onClick={() => toggle(p.id)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p className="truncate text-sm">{p.name}</p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      {p.output_quantity} un por impressão · {gramas(p.total_g)}
                    </p>
                  </button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => send([p.id])}
                    data-testid={`send-single-plate-btn-${p.id}`}
                    className="h-8 shrink-0 rounded-full text-[11px]"
                  >
                    Só esta
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <Button
          onClick={() => send(picked)}
          disabled={busy || plates.length === 0}
          data-testid="send-all-plates-btn"
          className="h-12 gap-2 rounded-full font-semibold transition-transform active:scale-95"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Factory className="h-4 w-4" />}
          Enviar {picked.length > 1 ? `${picked.length} Plates` : "para produção"}
        </Button>
      </div>
    </ResponsiveModal>
  );
}
